<template>
  <v-container>
    <v-card light class="pa-4">

      <v-img :src="require(`@/assets/articles/terminals/pma560_new_terminals_banner.jpg`)" height="260px"></v-img>

      <v-card-title>
        Denon PMA-560 New Speaker Terminals
      </v-card-title>


      <v-card-text>
        <p>After a test session comparing speakers (Stirling LS3/5a V2 and Magnepan MG 1.7i) and amplifiers
          (my modded Denon PMA560 and an Accuphase E-250) I got fed up with how hard it was to swap the speaker cables,
          and decided to finally do a relatively easy upgrade: new speaker terminals that take banana plugs.
        </p>
        <p>
          If you need extra info on any of the steps, don't hesitate to send me a note.
        </p>

        <p>
          <div class="text-h4">Parts</div><br>

          I ordered
          <a href="https://www.amazon.nl/dp/B08JHS9K54" target="_blank">"Jubaopen" banana plug terminals</a> and
          <a href="https://www.amazon.nl/dp/B0B5Y6VHSN" target="_blank">Nakamichi banana plugs</a> on Amazon Netherlands.<br/>
          They cost &euro; 8.99 (for a set of 6 terminals) and &euro; 12.00 (4 plugs) respectively.

          <br clear="left" />
          <div style="width:640px" class="float-left ma-2 mt-4 mb-4">
            <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/pma560_terminals_banana_plugs.jpg`)"></zoom-on-hover>
          </div>

        </p>
        <br clear="left" />

        <p>
          <div class="text-h4">The Steps</div><br>

          <ol>
            <li><span class='step_header'>remove back plate and determine hole locations</span><br/>
              I decided to put the new terminals above the current ones, mostly because the factory holes are too large for the new terminals.
              As you can see I wasn't super accurate when plotting the drill locations..
              <br clear="left">

              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/01_backplate.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/02_backplate.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">

            </li>
            <li><span class='step_header'>drill holes for new terminals</span><br/>
              I first punched indentations with a hammer+nail, to prevent slipping of the drill.
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/03_punch.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/04_punch.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">
              Next, pre-drilling with 3mm, then 6mm.
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/05_drill.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/06_drill.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">
              It seems the holes need to be about 1/4 inch in diameter, i.e. a bit more than 6mm.
              I widened them a bit by inserting a file and filing/turning.
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/07_hole.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/08_hole.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">
              The image below shows which parts of the terminal are on the outside of the amplifier, and which on the inside.<br/>
              The plastic bits that insulate the terminal from the case are in the middle.
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/09_terminal_parts.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/10_insulators.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">

            </li>

            <li><span class='step_header'>solder connecting wires onto new terminals, and solder connecting wires to old speaker terminals</span><br/>
              For the connecting wire I used a few cm Van den Hul CS-122 speaker wire, just because I had some lying around.<br/>
              Note that I first soldered the wire sections to the L-shaped bits of the terminals, and then to the fairly
              large speaker conductors below.
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/11_connecting_wire.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/12_wires_soldered.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">

            </li>

            <li><span class='step_header'>put new terminals in back plate</span><br/>
              Note you want them nice and tight, so you can unscrew the cable "clamp" if needed without the whole thing turning.<br/>
              The right picture shows an easy way to hold the outside of the terminal while fastening.
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/13_attach_terminals.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/14_attach_terminals.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/15_attach_terminals.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/16_attach_terminals.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">
            </li>

            <li><span class='step_header'>attach banana plugs to speaker wire</span><br/>
              Only easy steps left now <tt>:-)</tt><br/>
              My speaker wire (Van den Hul CS-122) apparently is ~ AWG 12.5,
              and the Nakamichi plugs can take AWG 12 to 18.<br/>
              It's nice the plugs have 2 screws, making for a very solid connection.
              The instructions are from Nakamichi (downloaded from the Amazon page).
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/17_nakamichi_instructions.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/18_nakamichi_plugs.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">
            </li>

            <li><span class='step_header'>plug them in <tt>:-)</tt></span><br/>
              Ok I might be imagining things but I think the amp sounds better with these new speaker connections.<br/>
              In the right picture you can see both the modded PMA560 and my original unmodded one that I bought in 1990.
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/19_plugged_in.jpg`)"></zoom-on-hover>
              </div>
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/20_two_pma_560.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">
            </li>

            <li><span class='step_header'>twist mains cable</span><br/>
              While the amp was open anyway, I decided to twist the part of mains cable that runs inside the case,
              based on this <a href="https://www.youtube.com/watch?v=y35H3zrKUbc" target="_blank">"Audio University" video</a>.<br/>
              If you have any opinions on this, please let me know!
              <br clear="left">
              <div class="picture_holder float-left ma-2 mt-4 mb-4">
                <zoom-on-hover :img-normal="require(`@/assets/articles/terminals/21_mains_twisted.jpg`)"></zoom-on-hover>
              </div>
              <br clear="left">
            </li>

          </ol>


        </p>

      </v-card-text>

    </v-card>
    <social />
  </v-container>
</template>

<script>
  export default {
    name: 'Terminals',

    components: {
      Social: () => import('@/components/home/Social')
    },

    methods: {
    }
  }
</script>

<style lang="scss" scoped>
.picture_holder
{
  width: 500px;
}

.step_header
{
  font-weight: bold;
}

</style>
